// src/components/DesktopHeader.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  TextField,
  Badge,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import { 
  Search as SearchIcon, 
  ShoppingBag,
  Menu as MenuIcon,
  Person,
  Inventory2,
  ReceiptLong,
  LocalShipping,
  Logout,
  Favorite,
} from '@mui/icons-material';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../services/supabaseClient';
import FourSquaresIcon from './FourSquaresIcon';

const DesktopHeader = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [localSearchTerm, setLocalSearchTerm] = useState(
    searchParams.get('search') || ''
  );
  const [categories, setCategories] = useState([]);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const { cartCount } = useCart();
  const { user, isAdmin, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isMenuOpen = Boolean(menuAnchorEl);

  // Determine current main department
  const getCurrentDepartment = () => {
    const path = location.pathname;
    if (path.includes('/department/men')) return 'الرجال';
    if (path.includes('/department/women')) return 'النساء';
    if (path.includes('/department/children')) return 'الأطفال';
    return 'الأطفال';
  };
  const currentDepartment = getCurrentDepartment();

  // Fetch all categories from Supabase
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const { data, error } = await supabase
          .from('categories')
          .select('id, name, parent_id')
          .order('id');
        if (error) throw error;
        setCategories(data);
      } catch (err) {
        console.error('Error fetching categories:', err);
      }
    };
    fetchCategories();
  }, []);

  // Derive subcategories for the current department
  const subcategories = React.useMemo(() => {
    const mainCat = categories.find(
      (cat) => cat.parent_id === null && cat.name === currentDepartment
    );
    if (!mainCat) return [];
    return categories.filter((cat) => cat.parent_id === mainCat.id);
  }, [categories, currentDepartment]);

  // Update search query param when typing or clicking search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (location.pathname.startsWith('/department/')) {
        const params = new URLSearchParams(searchParams);
        if (localSearchTerm) params.set('search', localSearchTerm);
        else params.delete('search');
        setSearchParams(params);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [localSearchTerm, searchParams, setSearchParams, location.pathname]);

  const handleSearchClick = () => {
    if (location.pathname.startsWith('/department/')) {
      const params = new URLSearchParams(searchParams);
      if (localSearchTerm) params.set('search', localSearchTerm);
      else params.delete('search');
      setSearchParams(params);
    }
  };

  const handleMenuOpen = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleLogout = async () => {
    await signOut();
    handleMenuClose();
    navigate('/');
  };

  const categoryButtonStyles = (name) => ({
    color: currentDepartment === name ? '#fff' : '#333',
    fontWeight: 'bold',
    fontSize: '18px',
    textTransform: 'none',
    backgroundColor: currentDepartment === name ? '#0f3662' : 'transparent',
    padding: '4px 16px',
    margin: '0 4px',
    '&:hover': {
      backgroundColor: 'rgba(15, 54, 98, 0.1)',
      color: '#0f3662',
    },
  });

  const getDepartmentPath = (arabicName) => {
    switch (arabicName) {
      case 'الرجال':
        return '/department/men';
      case 'النساء':
        return '/department/women';
      case 'الأطفال':
        return '/department/children';
      default:
        return '/department/children';
    }
  };

  const handleDepartmentClick = (department) => {
    const path = getDepartmentPath(department);
    const params = new URLSearchParams();
    if (localSearchTerm) params.set('search', localSearchTerm);
    const fullPath = params.toString() ? `${path}?${params.toString()}` : path;
    navigate(fullPath);
  };

  // Navigate to subcategory
  const handleSubcategoryClick = (sub) => {
    const base = getDepartmentPath(currentDepartment);
    const subPath = `${base}/${encodeURIComponent(sub.name)}`;
    const params = new URLSearchParams();
    if (localSearchTerm) params.set('search', localSearchTerm);
    const full = params.toString() ? `${subPath}?${params.toString()}` : subPath;
    navigate(full);
  };

  return (
    <>
      {/* Main Header */}
      <Box sx={{ backgroundColor: '#fff' }}>
        <Box
          sx={{ 
            maxWidth: '1400px', 
            mx: 'auto',
            display: 'flex',
            alignItems: 'center',
            px: { xs: 1, md: 3 },
            py: 0
          }}
        >
          <Link to="/department/children">
            <img src="/logo192.png" alt="Logo" height={125} />
          </Link>
          <Box mx={2.5} />
          <Box sx={{ display: 'flex', gap: 0 }}>
            <Button onClick={() => handleDepartmentClick('الأطفال')} sx={categoryButtonStyles('الأطفال')}>
              الأطفال
            </Button>
            <Button onClick={() => handleDepartmentClick('النساء')} sx={categoryButtonStyles('النساء')}>
              النساء
            </Button>
            <Button onClick={() => handleDepartmentClick('الرجال')} sx={categoryButtonStyles('الرجال')}>
              الرجال
            </Button>
          </Box>
          <Box mx={2.5} />
          <Box sx={{ width: 450 }}>
            <TextField
              fullWidth
              placeholder="ابحث في المتجر بالكامل هنا..."
              size="small"
              variant="outlined"
              value={localSearchTerm}
              onChange={(e) => setLocalSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearchClick()}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleSearchClick}
                      sx={{
                        bgcolor: '#0f3662',
                        color: 'white',
                        p: 1,
                        ml: -1,
                        zIndex: 1,
                        '&:hover': { bgcolor: '#0f3662' },
                        borderRadius: '0 4px 4px 0',
                      }}
                    >
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
                sx: {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderRadius: '4px 0 0 4px',
                  },
                },
              }}
              inputProps={{ dir: 'rtl', style: { textAlign: 'right' } }}
            />
          </Box>
          
          {/* Flexible spacing that adapts to content */}
          <Box sx={{ flexGrow: 1 }} />
          
          {/* Right side icons */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {!user ? (
              <Button
                color="inherit"
                onClick={() => navigate('/login')}
                sx={{ 
                  fontWeight: 'bold', 
                  fontSize: '16px',
                  textTransform: 'none',
                  color: '#0f3662',
                  '&:hover': { backgroundColor: 'rgba(15, 54, 98, 0.1)' },
                  whiteSpace: 'nowrap'
                }}
              >
                تسجيل حساب - تسجيل الدخول
              </Button>
            ) : null}
            
            {/* Shopping cart */}
            <IconButton color="inherit" component={Link} to="/cart">
              <Badge badgeContent={cartCount} color="secondary">
                <ShoppingBag sx={{ color: '#0f3662' }} />
              </Badge>
            </IconButton>
              
            {user && (
              // Hamburger menu for logged-in users
              <IconButton
                color="inherit"
                onClick={handleMenuOpen}
                sx={{
                  color: '#0f3662',
                  '&:hover': { backgroundColor: 'rgba(15, 54, 98, 0.1)' }
                }}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Box>
        </Box>
      </Box>

      {/* User Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={isMenuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            minWidth: 200,
            mt: 1,
            '& .MuiMenuItem-root': {
              direction: 'rtl',
              justifyContent: 'flex-end',
            }
          }
        }}
      >
        {/* Profile */}
        <MenuItem 
          component={Link} 
          to="/profile" 
          onClick={handleMenuClose}
          sx={{ gap: 1 }}
        >
          <ListItemText primary="حسابي" />
          <ListItemIcon sx={{ minWidth: 'auto' }}>
            <Person sx={{ color: '#0f3662' }} />
          </ListItemIcon>
        </MenuItem>

        {/* Categories - Available for all users */}
        <MenuItem 
          component={Link} 
          to="/categories" 
          onClick={handleMenuClose}
          sx={{ gap: 1 }}
        >
          <ListItemText primary="الفئات" />
          <ListItemIcon sx={{ minWidth: 'auto' }}>
            <FourSquaresIcon sx={{ color: '#0f3662' }} />
          </ListItemIcon>
        </MenuItem>

        {/* Favorites - Available for all users */}
        <MenuItem 
          component={Link} 
          to="/favorites" 
          onClick={handleMenuClose}
          sx={{ gap: 1 }}
        >
          <ListItemText primary="المفضلة" />
          <ListItemIcon sx={{ minWidth: 'auto' }}>
            <Favorite sx={{ color: '#0f3662' }} />
          </ListItemIcon>
        </MenuItem>

        {/* Admin menu items */}
        {isAdmin && (
          <>
            <Divider />
            <MenuItem 
              component={Link} 
              to="/admin/products" 
              onClick={handleMenuClose}
              sx={{ gap: 1 }}
            >
              <ListItemText primary="المنتجات" />
              <ListItemIcon sx={{ minWidth: 'auto' }}>
                <Inventory2 sx={{ color: '#0f3662' }} />
              </ListItemIcon>
            </MenuItem>
            <MenuItem 
              component={Link} 
              to="/admin/orders" 
              onClick={handleMenuClose}
              sx={{ gap: 1 }}
            >
              <ListItemText primary="الطلبات" />
              <ListItemIcon sx={{ minWidth: 'auto' }}>
                <ReceiptLong sx={{ color: '#0f3662' }} />
              </ListItemIcon>
            </MenuItem>
            <MenuItem 
              component={Link} 
              to="/admin/shipping" 
              onClick={handleMenuClose}
              sx={{ gap: 1 }}
            >
              <ListItemText primary="الشحن" />
              <ListItemIcon sx={{ minWidth: 'auto' }}>
                <LocalShipping sx={{ color: '#0f3662' }} />
              </ListItemIcon>
            </MenuItem>
          </>
        )}

        <Divider />
        <MenuItem onClick={handleLogout} sx={{ gap: 1 }}>
          <ListItemText primary="تسجيل الخروج" />
          <ListItemIcon sx={{ minWidth: 'auto' }}>
            <Logout sx={{ color: '#0f3662' }} />
          </ListItemIcon>
        </MenuItem>
      </Menu>

      {/* Sub Navigation */}
      {subcategories.length > 0 && (
        <Box sx={{ backgroundColor: '#fff', borderTop: '1px solid #e0e0e0' }}>
          <Box
            component="nav"
            sx={{
              maxWidth: '1400px',
              mx: 'auto',
              display: 'flex',
              gap: 2,
              px: { xs: 1, md: 3 },
              py: 0.5
            }}
          >
            {subcategories.map((sub) => (
              <Button
                key={sub.id}
                onClick={() => handleSubcategoryClick(sub)}
                sx={{
                  textTransform: 'none',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  color: '#a72c2a',
                  '&:hover': { color: '#0f3662' },
                }}
              >
                {sub.name}
              </Button>
            ))}
          </Box>
        </Box>
      )}
    </>
  );
};

export default DesktopHeader;
